import moment from 'moment'
import { get } from 'lodash'
import { path } from 'lib/utils/path'
import { BOS_STATUS, BOS_DISPUTE_STATUS, DISPUTE_BY, PGW_STATUS } from 'lib/bos'
import { assert, logException } from 'lib/errors'
import { getModalUrl } from 'lib/modals'
import { getLocale } from 'lib/utils/getLocale'

export const PAYMENT_METHOD = {
    ONLINE: 'online',
    CASH: 'cash',
    PARTIAL: 'partial',
}

export const TRANSFER_METHOD = {
    SELLER_SUBMIT: 'sellerSubmit',
    OTHER: 'other',
}

export const getStatusColor = (status) => {
    switch (status.toUpperCase()) {
    case BOS_STATUS.DRAFT:
        return 'orange-dark'
    case BOS_STATUS.PENDING:
    case BOS_STATUS.LOCKED:
    case BOS_STATUS.SIGNED:
    case BOS_STATUS.PAID:
    case BOS_STATUS.DELIVERED:
        return 'orange'
    case BOS_STATUS.CONFIRMED:
    case BOS_STATUS.COMPLETED:
        return 'green'
    case BOS_STATUS.DISPUTED:
        return 'red-dark'
    case BOS_STATUS.CANCELLED:
        return 'gray-dark'
    case BOS_STATUS.FINANCING:
    default:
        logException(`Invalid status on getStatusColor: ${status}`)
    }
}

export const getPgwBgColor = (status) => {
    switch (status.toUpperCase()) {
    case PGW_STATUS.ACTIVE:
        return 'off-white'
    case PGW_STATUS.PENDING:
    case PGW_STATUS.WAITING_APPROVAL:
    case PGW_STATUS.WAITING_REVIEW:
    case PGW_STATUS.BLOCKED:
        return 'beige'
    case PGW_STATUS.CLOSED:
        return 'pale-pink'
    default:
        logException(`Invalid status on getPgwBgColor: ${status}`)
    }
}

export const getPgwStatusColor = (status) => {
    switch (status.toUpperCase()) {
    case PGW_STATUS.ACTIVE:
        return 'lime-green'
    case PGW_STATUS.PENDING:
    case PGW_STATUS.WAITING_APPROVAL:
    case PGW_STATUS.WAITING_REVIEW:
    case PGW_STATUS.BLOCKED:
        return 'russet-brown'
    case PGW_STATUS.CLOSED:
        return 'dark-coral'
    default:
        logException(`Invalid status on getPgwStatusColor: ${status}`)
    }
}

export const getPaymentMethod = (bos, preferSigned = false) => {
    return (
        preferSigned && bos.signedBos
            ? bos.signedBos.pricePayment.paymentMethod
            : bos.pricePayment.paymentMethod
    )
}

export const isCashPayment = (bos, preferSigned = false) => {
    return getPaymentMethod(bos, preferSigned) === PAYMENT_METHOD.CASH
}

export const isOnlinePayment = (bos, preferSigned = false) => {
    return getPaymentMethod(bos, preferSigned) === PAYMENT_METHOD.ONLINE
}

export const isPartialPayment = (bos, preferSigned = false) => {
    return getPaymentMethod(bos, preferSigned) === PAYMENT_METHOD.PARTIAL
}

export const getTransferMethod = (bos) => {
    const method = bos.pricePayment.ownershipTransferMethod
    return method === 'sellerSubmit' ? TRANSFER_METHOD.SELLER_SUBMIT : TRANSFER_METHOD.OTHER
}

export const getBosEdited = (bos) => {
    return bos.updatedAt !== bos.createdAt
}

export const getBosDraftValid = (bos) => {
    return moment(bos.draftValidAt).isAfter(bos.updatedAt) || moment(bos.draftValidAt).isSame(bos.updatedAt)
}

export const getBosPath = (bosId, language, absolute = false) => {
    return path(`${getLocale(language)}/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}`, absolute)
}

export const getBosEditPath = (bosId, absolute = false) => {
    return path(`/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}/muokkaa`, absolute)
}

export const getBosSharePath = (bosId, linkHash, language, absolute = false) => {
    return path(`${getLocale(language)}/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}/jaa/${linkHash}`, absolute)
}

export const getBosPdfSharePath = (pdfUrl, linkHash, absolute = false) => {
    return path(`${pdfUrl}?checksum=${linkHash}`, absolute)
}

export const getBosViewPath = (bosId, absolute = false) => {
    return path(`/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}/katsele`, absolute)
}

export const getBosPreviewPath = (bosId, absolute = false) => {
    return path(`/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}/esikatselu`, absolute)
}

export const getBosPaymentPath = (bosId, absolute = false) => {
    return path(`/fiksut-kaupat/sahkoinen-kauppakirja/${bosId}/maksu/onnistunut`, absolute)
}

export const isBosActive = (bos) => {
    return bos && bos.status !== BOS_STATUS.DRAFT
}

export const isBosTransactionDone = (bos) => {
    return [BOS_STATUS.CONFIRMED, BOS_STATUS.COMPLETED].includes(bos.status)
}

export const bosCanBeDeleted = (bos) => {
    return [BOS_STATUS.DRAFT, BOS_STATUS.PENDING].includes(bos.status)
}

export const bosDeleteRequiresReason = (bos) => {
    if (bos.seller.sellerSignature === undefined) throw new Error('bosDeleteRequiresReason requires field "bos.seller.sellerSignature"')

    return !!bos.seller.sellerSignature || bos.status === BOS_STATUS.SIGNED
}

export const isBosDisputedWithoutActualPayment = (bos) => {
    return get(bos, 'payment.disputation.bosStatus', null) === BOS_STATUS.SIGNED
}

export const isBosDisputeSettled = (bos) => {
    return [BOS_STATUS.PAID, BOS_STATUS.DELIVERED].includes(get(bos, 'payment.disputation.bosStatus', null)) &&
        get(bos, 'payment.disputation.status', null) === BOS_DISPUTE_STATUS.BOS_CONFIRMED
}

export const isBosDisputedWithoutActualPaymentOrWithCash = (bos) => {
    return (bos.status === BOS_STATUS.SIGNED && !bos.payment) || isBosDisputedWithoutActualPayment(bos) || isCashPayment(bos)
}

export const getBosShareModalUrl = (location, bosId) => {
    return getModalUrl(location, `jaa/sahkoinen-kauppakirja/${bosId}`)
}

export const canShareBos = (bos) => {
    return [BOS_STATUS.DRAFT].includes(bos.status)
}

export const getBosProcessStepStatus = (bos) => {
    return bos.status === BOS_STATUS.DISPUTED
        ? bos.payment.disputation.bosStatus
        : bos.status
}

export const getSellerOwnershipConsentText = (t, sellerFlowType, signatoryName, sellerName) => {
    const text = t(`billOfSale:section.seller.fields.sellerOwnershipConsent.${sellerFlowType}.label`, {
        signatoryName,
        sellerName
    })
    assert(text, `Missing sellerOwnershipConsent translation for flow type "${sellerFlowType}"`)
    return text
}

export const getBosDisputeByName = (t, bos, disputeBy) => {
    switch (disputeBy) {
    case DISPUTE_BY.BUYER_ADMIN:
    case DISPUTE_BY.SELLER_ADMIN:
        return t('common:customerService')
    case DISPUTE_BY.SELLER:
        return bos.seller.sellerFirstName + ' ' + bos.seller.sellerLastName
    case DISPUTE_BY.BUYER:
        return bos.buyer.buyerFirstName + ' ' + bos.buyer.buyerLastName
    case DISPUTE_BY.FINANCE_USER:
        return 'Rahoitusyhtiö'
    default:
        throw new Error(`Invalid disputedBy value "${disputeBy}"`)
    }
}
